package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.VisibilityThreshold
import androidx.compose.animation.core.spring
import androidx.compose.animation.expandIn
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.shrinkOut
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeState
import kotlinx.coroutines.delay
import com.bikompanion.ui.home.RefreshInterval
import com.bikompanion.ui.models.DataFreshness
import com.bikompanion.ui.models.RefreshStatus
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.strings.LocalStrings
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime

private val RingSize = 14.dp
private val RingStroke = 2.dp

// Whatever sits under the pill in the status column is pushed down by its height, so that
// settles instead of bouncing. No bounce on the scale either: an overshoot past zero flips the
// pill inside out on its last frame.
private val PillSizeSpring = spring<IntSize>(
    dampingRatio = Spring.DampingRatioNoBouncy,
    stiffness = Spring.StiffnessMediumLow,
    visibilityThreshold = IntSize.VisibilityThreshold,
)
private val PillScaleSpring = spring<Float>(
    dampingRatio = Spring.DampingRatioNoBouncy,
    stiffness = Spring.StiffnessMediumLow,
)

// Faster than the shrink: the pill is mostly gone before the last, smallest frames of it, which
// is what stops the tail end of the scale reading as a speck left behind on the map.
private val PillFadeSpring = spring<Float>(stiffness = Spring.StiffnessMedium)

// The pill hangs off the top-start corner of the map, so it shrinks into that corner rather than
// collapsing about its own middle and looking like it slid off somewhere.
private val PillShrinkCorner = Alignment.TopStart

// The same corner the layout shrinks towards. Scaling and shrinking together is what keeps the
// capsule looking like a capsule: `shrinkOut` alone just crops the layout box, so the pill spends
// the animation as a squared-off slice of itself with its text cut through the middle.
private val PillShrinkOrigin = TransformOrigin(pivotFractionX = 0f, pivotFractionY = 0f)

/** Below this, "updated 4s ago" is noise — it just means the poll landed. */
private val JustNow = 45.seconds

/**
 * Says when the numbers on screen were last worth trusting, and when they'll be checked again.
 *
 * Availability changes without anything on screen moving, so without this the map looks the
 * same whether it is thirty seconds old or has quietly stopped updating altogether. When a
 * refresh does fail the countdown alone would be actively misleading — it keeps promising an
 * update that isn't arriving — so the pill switches to saying how old what you're looking at
 * really is.
 */
@Composable
internal fun RefreshIndicator(
    status: RefreshStatus,
    freshness: DataFreshness,
    hazeState: HazeState,
    modifier: Modifier = Modifier,
) {
    // Nothing is being polled and nothing is wrong, so there is no next update to promise.
    // A failure still gets a pill even when the loop has stopped: the numbers are on screen
    // either way, and their age is the whole point.
    val isVisible = status !is RefreshStatus.Idle || freshness.isStale

    AnimatedVisibility(
        visible = isVisible,
        enter = expandIn(PillSizeSpring, expandFrom = PillShrinkCorner) +
            scaleIn(PillScaleSpring, transformOrigin = PillShrinkOrigin) +
            fadeIn(PillFadeSpring),
        exit = shrinkOut(PillSizeSpring, shrinkTowards = PillShrinkCorner) +
            scaleOut(PillScaleSpring, transformOrigin = PillShrinkOrigin) +
            fadeOut(PillFadeSpring),
    ) {
        // The caller's modifier goes on the pill itself rather than on the AnimatedVisibility
        // above, so the spacing it carries collapses with the pill instead of leaving a gap
        // where the pill used to be.
        GlassPill(hazeState = hazeState, modifier = modifier) {
            Row(
                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                val total = RefreshInterval.inWholeSeconds.toFloat()
                val remaining = when (status) {
                    is RefreshStatus.Waiting -> (status.secondsRemaining / total).coerceIn(0f, 1f)
                    else -> 1f
                }
                CountdownRing(
                    remaining = remaining,
                    color = if (freshness.isStale) {
                        MaterialTheme.colorScheme.error
                    } else {
                        MaterialTheme.colorScheme.primary
                    },
                )
                Column {
                    Text(
                        text = when {
                            freshness.isRateLimited -> LocalStrings.current.refreshRateLimited
                            freshness.isStale -> LocalStrings.current.refreshFailed
                            status is RefreshStatus.Waiting -> LocalStrings.current.refreshNextUpdate(status.secondsRemaining)
                            else -> LocalStrings.current.refreshUpdating
                        },
                        style = MaterialTheme.typography.labelMedium,
                    )
                    // Only worth the second line when the first one is bad news; while refreshes
                    // are landing, "next update in 12s" already says the data is current.
                    if (freshness.isStale) {
                        LastUpdatedLabel(freshness.updatedAtEpochMillis)
                    }
                }
            }
        }
    }
}

/**
 * "Showing data from 6 min ago", ticking on its own.
 *
 * It has to tick: once refreshes stop landing nothing else in the pill changes, and a label
 * frozen at "1 min ago" while the data quietly ages to an hour is the same lie the pill is
 * here to avoid.
 */
@OptIn(ExperimentalTime::class)
@Composable
private fun LastUpdatedLabel(updatedAtEpochMillis: Long?) {
    val text = if (updatedAtEpochMillis == null) {
        // Nothing has ever loaded — a first run with no connection and an empty cache.
        LocalStrings.current.dataNone
    } else {
        val now by produceState(Clock.System.now().toEpochMilliseconds(), updatedAtEpochMillis) {
            while (true) {
                delay(OneSecondTick)
                value = Clock.System.now().toEpochMilliseconds()
            }
        }
        dataAgeText(now - updatedAtEpochMillis)
    }
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

private const val OneSecondTick = 1_000L

/**
 * How long ago, in the coarsest unit that still says something useful.
 *
 * Wall clock rather than a monotonic mark, because the timestamp can come off disk from a
 * previous run — the only clock both sides of that share is the calendar. The cost is that a
 * device whose clock has been moved can produce a negative age, which reads as "just now".
 */
@Composable
private fun dataAgeText(ageMillis: Long): String {
    val age = ageMillis.milliseconds
    return when {
        age < JustNow -> LocalStrings.current.dataAgeJustNow
        age < 1.hours -> LocalStrings.current.dataAgeMinutes(age.inWholeMinutes.toInt())
        age < 1.days -> LocalStrings.current.dataAgeHours(age.inWholeHours.toInt())
        else -> LocalStrings.current.dataAgeDays(age.inWholeDays.toInt())
    }
}

/** A ring that empties as the wait runs down. */
@Composable
private fun CountdownRing(
    remaining: Float,
    color: Color,
) {
    val trackColor = MaterialTheme.colorScheme.surfaceVariant
    Canvas(modifier = Modifier.size(RingSize)) {
        val stroke = Stroke(RingStroke.toPx())
        val inset = stroke.width / 2f
        val arcSize = Size(
            size.width - stroke.width,
            size.height - stroke.width,
        )
        val topLeft = Offset(inset, inset)
        drawArc(
            color = trackColor,
            startAngle = 0f,
            sweepAngle = 360f,
            useCenter = false,
            topLeft = topLeft,
            size = arcSize,
            style = stroke,
        )
        drawArc(
            color = color,
            startAngle = -90f,
            sweepAngle = 360f * remaining,
            useCenter = false,
            topLeft = topLeft,
            size = arcSize,
            style = stroke,
        )
    }
}

@Preview
@Composable
private fun RefreshIndicatorHealthyPreview() {
    GlassPreviewSurface { hazeState ->
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            RefreshIndicator(RefreshStatus.Waiting(secondsRemaining = 24), DataFreshness(), hazeState)
            RefreshIndicator(RefreshStatus.Waiting(secondsRemaining = 3), DataFreshness(), hazeState)
            RefreshIndicator(RefreshStatus.Refreshing, DataFreshness(), hazeState)
        }
    }
}

/** One per unit the age can read in, then the two failures that have no age to give. */
@OptIn(ExperimentalTime::class)
@Preview
@Composable
private fun RefreshIndicatorStalePreview() {
    val now = Clock.System.now().toEpochMilliseconds()
    GlassPreviewSurface { hazeState ->
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            listOf(10.seconds, 6.minutes, 3.hours, 2.days).forEach { age ->
                RefreshIndicator(
                    status = RefreshStatus.Idle,
                    freshness = DataFreshness(updatedAtEpochMillis = now - age.inWholeMilliseconds, isStale = true),
                    hazeState = hazeState,
                )
            }
            RefreshIndicator(status = RefreshStatus.Idle, freshness = DataFreshness(isStale = true), hazeState = hazeState)
            RefreshIndicator(
                status = RefreshStatus.Idle,
                freshness = DataFreshness(updatedAtEpochMillis = now, isStale = true, isRateLimited = true),
                hazeState = hazeState,
            )
        }
    }
}
